import React from 'react';
import {Select, MenuItem, styled} from '@mui/material';
import type {SelectChangeEvent} from '@mui/material';
import {Headline, designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {ToneOption} from '../../../constants/toneOptions';

interface ToneOfVoiceDropdownProps {
  testID?: string;
  value: string;
  options: ToneOption[];
  onChange: (value: string) => void;
  placeholder?: string;
}

const StyledSelect = styled(Select)(() => ({
  'width': '100%',
  'height': '40px',
  'borderRadius': '8px',
  'backgroundColor': designSystemToken('semantic.bg.primary'),
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: designSystemToken('semantic.bg.neutral-inverse'),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: designSystemToken('semantic.fg.primary'),
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: designSystemToken('semantic.bg.neutral-inverse'),
    borderWidth: '1px',
  },
  '& .MuiSelect-select': {
    padding: '8px 12px',
  },
}));

const StyledMenuItem = styled(MenuItem)(() => ({
  'width': '100%',
  'minHeight': '30px',
  'height': '30px',
  'display': 'flex',
  'alignItems': 'center',
  '&.MuiMenuItem-root': {
    padding: '6px 16px',
  },
  '&.Mui-selected': {
    backgroundColor: designSystemToken('semantic.bg.tertiary'),
  },
  '&.MuiMenuItem-root:hover': {
    backgroundColor: designSystemToken('semantic.bg.tertiary'),
  },
}));

function ToneOfVoiceDropdown({
  testID = 'tone-of-voice-dropdown',
  value,
  options,
  onChange,
  placeholder = 'Select tone...',
}: ToneOfVoiceDropdownProps) {
  const handleChange = (event: SelectChangeEvent<unknown>) => {
    onChange(event.target.value as string);
  };

  return (
    <StyledSelect
      value={value}
      onChange={handleChange}
      displayEmpty
      data-testid={testID}
      MenuProps={{
        sx: {
          '& .MuiPaper-root': {
            marginTop: '8px',
          },
        },
      }}
      renderValue={(selected) => {
        if (!selected) {
          return (
            <Headline size="xxs" sx={{fontWeight: 400}}>
              {translate(placeholder)}
            </Headline>
          );
        }
        const selectedOption = options.find((option) => option.id === selected);
        return (
          <Headline size="xxs" sx={{fontWeight: 500}}>
            {selectedOption ? translate(selectedOption.localeLabelKey) : ''}
          </Headline>
        );
      }}
    >
      {options.map((option) => (
        <StyledMenuItem key={option.id} value={option.id}>
          <Headline size="xxs" sx={{fontWeight: 400}}>
            {translate(option.localeLabelKey)}
          </Headline>
        </StyledMenuItem>
      ))}
    </StyledSelect>
  );
}

export default ToneOfVoiceDropdown;
